import { Check, Loader2, Search } from "lucide-react";
import Segmented from "@/components/enhance/Segmented";
import { clock } from "@/lib/voiceSongsApi";

const VERSION_LABELS = { polished: "Studio-Polished", natural: "Natural", original: "Original" };

function Row({ id, title, sub, active, onClick, children }) {
  return (
    <li>
      <button type="button" data-testid={`bgv-source-${id}`} aria-pressed={active} onClick={onClick}
        className={`flex w-full items-center gap-3 rounded-2xl border px-3.5 py-2.5 text-left transition-colors duration-150 ${active ? "border-violet-300/50 bg-violet-400/10" : "border-transparent hover:bg-white/[0.05]"}`}>
        <span className={`grid h-5 w-5 flex-shrink-0 place-items-center rounded-full border ${active ? "border-violet-200 bg-violet-300 text-black" : "border-white/25"}`}>{active ? <Check className="h-3 w-3" /> : null}</span>
        <span className="min-w-0 flex-1"><span className="block truncate text-[13px] font-medium">{title}</span><span className="block truncate text-[11px] text-white/45">{sub}</span></span>
      </button>
      {active ? children : null}
    </li>
  );
}

function rowsFor(tab, sources) {
  if (tab === "enhanced") return sources.enhanced.map((e) => ({ key: e.id, title: e.title, sub: `${clock(e.duration)} · enhanced vocal`, pick: { kind: "enhance", id: e.id, version: e.versions[0] || "polished", versions: e.versions, label: e.title, has_music: false } }));
  if (tab === "songs") return sources.songs.map((s) => ({ key: s.id, title: s.title, sub: `${clock(s.duration)} · ${s.has_parts ? "parts ready — no splitting needed" : "the lead is split from the music first (1–3 min, a few cents)"}`, pick: { kind: "library", id: s.id, label: s.title, has_music: true } }));
  return sources.takes.map((t) => ({ key: t.take_id, title: `${t.take_name || "Take"} — ${t.session_title}`, sub: `${t.track_name || "Vocal"} · ${clock(t.duration)}${t.has_music ? " · recorded over music" : ""}`, pick: { kind: "take", session_id: t.session_id, track_id: t.track_id, take_id: t.take_id, label: t.take_name, has_music: t.has_music } }));
}

const same = (a, b) => a && b && a.kind === b.kind && (a.id || a.take_id) === (b.id || b.take_id);

/** The leads you can add singers to, for the chosen tab (songs are searchable). */
export default function SourceList({ tab, sources, pick, onPick, q, onSearch }) {
  if (!sources) return <Loader2 className="h-5 w-5 animate-spin text-white/40" />;
  const rows = rowsFor(tab, sources);
  const preset = pick && !rows.some((r) => same(r.pick, pick)) && (tab === "songs" ? pick.kind === "library" : tab === "enhanced" ? pick.kind === "enhance" : pick.kind === "take");
  return (
    <div>
      {tab === "songs" ? (
        <label className="mb-2 flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3.5 py-2">
          <Search className="h-4 w-4 text-white/40" />
          <input data-testid="bgv-song-search" value={q} onChange={(e) => onSearch(e.target.value)} placeholder="Search your songs" className="w-full bg-transparent text-[13px] text-white placeholder:text-white/35 focus:outline-none" />
        </label>
      ) : null}
      <ul data-testid={`bgv-sources-${tab}`} className="max-h-72 space-y-1 overflow-y-auto pr-1">
        {preset ? <Row id="preset" title={pick.label} sub="Chosen from the page you came from" active onClick={() => {}} /> : null}
        {rows.map((r) => (
          <Row key={r.key} id={r.key} title={r.title} sub={r.sub} active={same(r.pick, pick)} onClick={() => onPick(r.pick)}>
            {same(r.pick, pick) && r.pick.versions?.length > 1 ? (
              <div className="px-3.5 pb-2 pt-2">
                <Segmented label="Use this version" value={pick.version} options={r.pick.versions.map((v) => [v, VERSION_LABELS[v]])} onChange={(v) => onPick({ ...pick, version: v })} testId="bgv-source-version" />
              </div>
            ) : null}
          </Row>
        ))}
        {!rows.length && !preset ? <li data-testid="bgv-sources-empty" className="px-2 py-3 text-[13px] text-white/45">{tab === "enhanced" ? "No enhanced vocals yet — try Enhance My Vocals first." : tab === "takes" ? "No Recording Studio takes yet." : "No songs found."}</li> : null}
      </ul>
    </div>
  );
}
